'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CheckboxField, TextAreaField, TextField } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Feedback';
import { Icon } from '@/components/ui/Icon';
import { AFFILIATE_PLATFORMS } from '@/data/affiliates';
import { useLeadSubmission } from '@/hooks/useLeadSubmission';
import { hasErrors, isBlank, isEmail, messages, type ValidationErrors } from '@/lib/validation';

import { SubmissionError, SubmissionSuccess } from './FormFeedback';

interface AffiliateFormValues {
  name: string;
  email: string;
  phone: string;
  business: string;
  location: string;
  message: string;
  platforms: string;
}

function validate(values: AffiliateFormValues): ValidationErrors<AffiliateFormValues> {
  return {
    name: isBlank(values.name) ? messages.required : undefined,
    email: isBlank(values.email) ? messages.required : isEmail(values.email) ? undefined : messages.email,
    business: isBlank(values.business) ? messages.required : undefined,
    platforms: isBlank(values.platforms) ? 'Select at least one platform.' : undefined,
  };
}

/** Task 3: request affiliate codes for the licensed platforms we market. */
export function AffiliateCodeForm({ defaultPlatformIds = [] }: { defaultPlatformIds?: string[] }) {
  const [selected, setSelected] = useState<string[]>(defaultPlatformIds);
  const [values, setValues] = useState<Omit<AffiliateFormValues, 'platforms'>>({
    name: '',
    email: '',
    phone: '',
    business: '',
    location: '',
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors<AffiliateFormValues>>({});
  const submission = useLeadSubmission();

  const setValue = <K extends keyof Omit<AffiliateFormValues, 'platforms'>>(key: K, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const togglePlatform = (id: string, checked: boolean) => {
    setSelected((current) => (checked ? [...current, id] : current.filter((item) => item !== id)));
    setErrors((current) => ({ ...current, platforms: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const platformNames = AFFILIATE_PLATFORMS.filter((platform) => selected.includes(platform.id))
      .map((platform) => platform.name)
      .join(', ');

    const nextErrors = validate({ ...values, platforms: platformNames });
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;

    await submission.submit({
      type: 'affiliate',
      name: values.name,
      email: values.email,
      phone: values.phone,
      business: values.business,
      location: values.location,
      message: values.message,
      details: { platforms: platformNames },
    });
  };

  if (submission.status === 'success') {
    return (
      <SubmissionSuccess
        title="Code request received"
        description="We will verify your details and issue your affiliate codes. Once approved, your codes are loaded onto your LaunchPad automatically — no reconfiguration needed on your side."
        onReset={submission.reset}
        resetLabel="Request another code"
      />
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <fieldset className="flex flex-col gap-3">
          <legend className="text-content-muted mb-2 text-sm font-medium">
            Which platforms do you want codes for? <span className="text-brand-bright">*</span>
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            {AFFILIATE_PLATFORMS.map((platform) => (
              <div
                key={platform.id}
                id={`request-${platform.id}`}
                className="border-line-strong/60 bg-ink/40 scroll-mt-28 rounded-xl border p-4"
              >
                <CheckboxField
                  checked={selected.includes(platform.id)}
                  onChange={(checked) => togglePlatform(platform.id, checked)}
                  label={
                    <>
                      <span className="font-semibold text-white">{platform.name}</span>
                      <span className="text-content-subtle block text-xs">{platform.category}</span>
                    </>
                  }
                />
              </div>
            ))}
          </div>
          {errors.platforms ? <p className="text-danger text-xs font-medium">{errors.platforms}</p> : null}
        </fieldset>

        <div className="grid gap-6 sm:grid-cols-2">
          <TextField
            label="Your name"
            required
            autoComplete="name"
            value={values.name}
            error={errors.name}
            onChange={(event) => setValue('name', event.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={values.email}
            error={errors.email}
            onChange={(event) => setValue('email', event.target.value)}
          />
          <TextField
            label="Business name"
            required
            autoComplete="organization"
            value={values.business}
            error={errors.business}
            onChange={(event) => setValue('business', event.target.value)}
          />
          <TextField
            label="Phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={values.phone}
            onChange={(event) => setValue('phone', event.target.value)}
          />
          <TextField
            label="City & state"
            className="sm:col-span-2"
            autoComplete="address-level2"
            value={values.location}
            onChange={(event) => setValue('location', event.target.value)}
          />
        </div>

        <TextAreaField
          label="Anything else?"
          rows={4}
          placeholder="Do you already have a LaunchPad? How many locations?"
          value={values.message}
          onChange={(event) => setValue('message', event.target.value)}
        />

        <SubmissionError submission={submission} />

        <Button type="submit" size="lg" disabled={submission.status === 'submitting'} className="self-start">
          {submission.status === 'submitting' ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            <>
              Request my affiliate codes
              <Icon name="arrow" className="h-5 w-5" />
            </>
          )}
        </Button>
      </form>
    </Card>
  );
}
